<template>
  <!-- A dark band (the general form of StoryGuardrails): a scene that plays once in view on one
       side, the rules on the other. `chapter.scene` names the scene; each is its own lazy chunk.
       Scenes are drawn in the app's dark theme on this band; resting styles are the final frame. -->
  <section class="sbd">
    <div class="sbd-shell sbd-grid">
      <div class="sbd-scene" aria-hidden="true">
        <LazyStorySceneReminders v-if="chapter.scene === 'reminders'" />
        <LazyStorySceneFlashPay v-else-if="chapter.scene === 'flashpay'" />
        <LazyStorySceneProposalActivity v-else-if="chapter.scene === 'proposal-activity'" />
        <LazyStorySceneContractHistory v-else-if="chapter.scene === 'contract-history'" />
        <LazyStorySceneRetainerConsent v-else-if="chapter.scene === 'retainer-consent'" />
        <LazyStoryScenePortalCode v-else-if="chapter.scene === 'portal-code'" />
        <LazyStoryScenePlanRecord v-else-if="chapter.scene === 'plan-record'" />
        <!-- Google busy time on a booking page: the Google Calendar page's hero scene, on its motion stage. -->
        <LazyMotionStage v-else-if="chapter.scene === 'gcal-busy'" :config="featureMotion['integrations/google-calendar']" />
      </div>
      <div class="sbd-copy">
        <h2>{{ chapter.title }}</h2>
        <p class="sbd-intro">{{ chapter.intro }}</p>
        <dl class="sbd-rules">
          <div v-for="rule in chapter.rules" :key="rule.title">
            <dt>{{ rule.title }}</dt>
            <dd>{{ rule.copy }}</dd>
          </div>
        </dl>
        <NuxtLink v-if="chapter.path" :to="localePath(chapter.path)" class="sbd-link">
          {{ chapter.linkLabel }} <span aria-hidden="true">-&gt;</span>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  chapter: { type: Object, required: true },
});
import { featureMotion } from "~/data/feature-motion.js";

const { localePath } = useWorkspaceLocale();
</script>

<style scoped>
.sbd { background: #0b1220; padding: 6.5rem 0; color: #e2e8f0; }
.sbd-shell { width: min(100% - 3rem, 84rem); margin: 0 auto; }
.sbd-grid { display: grid; gap: 2.5rem; align-items: center; }
@media (min-width: 1024px) { .sbd-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 4.5rem; } }
.sbd-copy h2 { color: #f8fafc; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.sbd-intro { margin-top: 1rem; color: #cbd5e1; font-size: 1rem; line-height: 1.7; }
.sbd-rules { display: grid; gap: 0; margin-top: 1.75rem; border-top: 1px solid #1e293b; }
.sbd-rules > div { display: grid; gap: 0.3rem; border-bottom: 1px solid #1e293b; padding: 1rem 0; }
@media (min-width: 640px) { .sbd-rules > div { grid-template-columns: 12rem 1fr; gap: 1.5rem; } }
.sbd-rules dt { color: #f8fafc; font-weight: 800; }
.sbd-rules dd { color: #cbd5e1; font-size: 0.95rem; line-height: 1.6; }
.sbd-link { display: inline-flex; gap: 0.4rem; margin-top: 1.25rem; color: #67e8f9; font-weight: 800; }
.sbd-link:hover { text-decoration: underline; }
.sbd-scene {
  border-radius: 14px;
  background:
    radial-gradient(circle, rgb(255 255 255 / 0.1) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(120% 120% at 50% 40%, rgb(27 167 206 / 0.18), rgb(27 167 206 / 0) 70%);
  padding: 2rem;
}
@media (max-width: 1023px) { .sbd-scene { order: 2; } }
@media (max-width: 639px) {
  .sbd { padding: 4.5rem 0; }
  .sbd-shell { width: min(100% - 2rem, 84rem); }
  .sbd-scene { padding: 0.9rem; }
}
</style>
